import { Box, Flex, HStack, VStack } from '@chakra-ui/layout';
import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  IconButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { SelfAccreditationClaims } from '@common/models/InvestorAccountModel/types';
import React, { useState } from 'react';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { useSurveyBannerModel } from '../models/SurveyBannerModel';

type AccreditationProps = {
  modalProps: UseDisclosureProps;
};

export const Accreditation: React.FC<AccreditationProps> = ({ modalProps }) => {
  const [showError, setShowError] = useState(false);
  const [claims, setClaims] = useState<SelfAccreditationClaims>({
    certification: false,
    employment: false,
    income: false,
    netWorth: false,
    none: false,
  });

  const model = useSurveyBannerModel();
  const { updateAccreditationInformation } = useInvestorAccountModel();

  const onChange = (claim: keyof SelfAccreditationClaims) => {
    setShowError(false);
    if (claim === 'none') {
      setClaims({ certification: false, employment: false, income: false, netWorth: false, none: true });
    } else {
      const newClaims = { ...claims };
      newClaims[claim] = !claims[claim];
      if (!newClaims.certification && !newClaims.employment && !newClaims.income && !newClaims.netWorth) {
        newClaims.none = true;
      } else {
        newClaims.none = false;
      }
      setClaims(newClaims);
    }
  };

  const save = async () => {
    model.setLoading(true);
    const result = await updateAccreditationInformation(claims);
    model.setLoading(false);
    if (result.success) {
      setShowError(false);
      model.nextStep();
    } else {
      setShowError(true);
    }
  };

  return (
    <ModalContent>
      <ModalHeader
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        borderBottomWidth={'1px'}
        borderBottomColor={'gray.300'}>
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="chevron-left" />}
          onClick={() => model.prevStep()}
        />
        Accreditation
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="times" />}
          onClick={modalProps.onClose}
        />
      </ModalHeader>
      <ModalBody>
        <Flex flex={1} gap="24px" p={'24px'} justifyContent={'space-between'} flexDirection={'column'} display={'flex'}>
          <TextBlock weight="bold" fontSize={'20px'} lineHeight={'20px'}>
            Are you an accredited investor?
          </TextBlock>
          <VStack letterSpacing="0.5px" fontWeight="600" gap="6px" alignItems="flex-start">
            <HStack
              cursor="pointer"
              onClick={() => {
                onChange('none');
              }}
              gap="20px"
              alignItems="flex-start">
              <Checkbox pointerEvents={'none'} isChecked={claims.none} mt="4px" />
              <TextBlock>No, I am not an accredited investor.</TextBlock>
            </HStack>
            <HStack
              cursor="pointer"
              onClick={() => {
                onChange('income');
              }}
              gap="20px"
              alignItems="flex-start">
              <Checkbox pointerEvents={'none'} isChecked={claims.income} mt="4px" />
              <TextBlock>
                Yes, I am an individual who has an income greater than $200,000 in each of the past two years or whose
                joint income with a spouse is greater than $300,000 for those years, and a reasonable expectation of the
                same income level in the current year.
              </TextBlock>
            </HStack>
            <HStack
              cursor="pointer"
              onClick={() => {
                onChange('netWorth');
              }}
              gap="20px"
              alignItems="flex-start">
              <Checkbox pointerEvents={'none'} isChecked={claims.netWorth} mt="4px" />
              <TextBlock>
                Yes, I am an individual who has a net worth, or joint net worth with the person’s spouse, that exceeds
                $1 million at the time of the investment (The net worth amount cannot include the value of the person's
                primary residence.)
              </TextBlock>
            </HStack>
            <HStack
              cursor="pointer"
              onClick={() => {
                onChange('certification');
              }}
              gap="20px"
              alignItems="flex-start">
              <Checkbox pointerEvents={'none'} isChecked={claims.certification} mt="4px" />
              <TextBlock>
                Yes, I am an individual who hold certain certificates, designations, or credentials, such as Series 7,
                Series 65, and Series 82 licenses.
              </TextBlock>
            </HStack>
            <HStack
              cursor="pointer"
              onClick={() => {
                onChange('employment');
              }}
              gap="20px"
              alignItems="flex-start">
              <Checkbox pointerEvents={'none'} isChecked={claims.employment} mt="4px" />
              <TextBlock>
                Yes, I am an individual who is a "knowledgeable employee" and affiliated with the offerings on the
                platform.
              </TextBlock>
            </HStack>
          </VStack>
          <Box justifyContent="center" px={'5px'}>
            {showError && (
              <Alert status="error">
                <AlertIcon />
                There was an error processing your request, please try again
              </Alert>
            )}
          </Box>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          autoFocus={true}
          isLoading={model.loading}
          isDisabled={false}
          w={'100%'}
          rightIcon={<Icon icon="chevron-right" />}
          mt={'10px'}
          onClick={save}>
          Continue
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};
